

















import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import PieChart from '@/components/charts/PieChart.vue';
import { AnalyticData } from '@/interfaces/analyticData';
import {
  getUnitMeasure,
  isValueInBucket,
  isValueInBucketCropMonitoring,
  parcelMatchFilters
} from '@/services/analyticData';
import { m2ToHa } from '@/services/units';
import { AnalyticType } from '@/enums/analyticType';
import { Parcel } from '@/interfaces/parcel';
import { stringToMomentDate } from '@/services/date';
import { ProductType } from '@/enums/productType';

interface HarvestedInfo {
  harvested: {
    [key: string]: Parcel;
  };
  partialHarvested: {
    [key: string]: Parcel;
  };
}

@Component({
  components: {
    PieChart
  }
})
export default class AnalyticsPieChart extends Vue {
  @Prop() analytics: AnalyticData[];
  @Prop() label: string;
  @Prop() bucket: any;
  @Prop() cropType: string;

  TYPE = {
    AREA: 'area',
    NUMBER: 'number',
    CROP_GROWTH: 'crop_growth'
  };

  selectedType: string = this.TYPE.NUMBER;
  selectedMetric: string = this.TYPE.CROP_GROWTH;

  @Watch('cropType')
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  onCropTypeChange(newValue: string) {
    this.selectedMetric = newValue || this.TYPE.CROP_GROWTH;
    this.setupChartData();
  }

  chartData = null;
  chartOptions = {
    maintainAspectRatio: false,
    legend: {
      display: true,
      position: 'right',
      labels: {
        padding: 12,
        usePointStyle: true,
        fontSize: 14,
        fontColor: '#161C26',
        fontFamily: 'Inter'
      }
    },
    tooltips: {
      enabled: true
    }
  };

  mounted(): void {
    this.setupChartData();
  }

  get isCropMonitoringDetailed(): boolean {
    return this.$store.state.analytic.selectedProductType === ProductType.VEGETATIVE_INDEX;
  }

  get measure(): string {
    return getUnitMeasure(this.$store.state.analytic.selectedAnalyticType);
  }

  @Watch('analytics')
  onAnalyticsChanged(): void {
    this.$nextTick(() => this.setupChartData());
  }

  deferSetupChartData(): void {
    setTimeout(() => this.setupChartData(), 0);
  }

  setupChartData(): void {
    if (this.analytics.length) {
      const data = this.getChartData();
      const harvestedInfo = this.getHarvestedInfo();
      const t1 = this.bucketLabels;
      const dataValues = this.formatValues(Object.values(data));

      let labels;
      if (this.$store.state.analytic.selectedProductType === ProductType.VEGETATIVE_INDEX) {
        const categoryNames = ['Poor', 'Average', 'Good', 'Excellent'];

        const total = dataValues.reduce((sum: number, value: number) => sum + value, 0);
        labels = dataValues.map((value: number, index: number) => {
          const percentage = total > 0 ? ((value / total) * 100).toFixed(0) : '0';
          return `${percentage}% ${categoryNames[index]}`;
        });
      } else {
        labels = t1.concat(`Harvested`);
      }

      this.chartData = {
        datasets: [
          {
            datalabels: {
              color: '#161c26'
            },
            data: dataValues,
            backgroundColor: Object.keys(data)
          }
        ],
        labels: labels
      };
    } else {
      this.chartData = {
        datasets: [],
        labels: []
      };
    }
  }

  private formatValues(values): any {
    if (this.selectedType === this.TYPE.AREA) {
      return values.map((value) => (value !== null ? m2ToHa(value) : null));
    }
    return values;
  }

  private getValueFromAnalytic(analytic: AnalyticData): number {
    if (this.isCropMonitoringDetailed && typeof analytic.Value === 'object') {
      return analytic.Value[this.selectedMetric]?.['value'] || 0;
    }
    return analytic.Value as number;
  }

  private getChartData(): any {
    const data = {};
    Object.keys(this.bucket).forEach((color: string) => {
      let total = 0;
      this.analytics.forEach((analytic: AnalyticData) => {
        const value = this.getValueFromAnalytic(analytic);
        if (
          this.$store.state.analytic.selectedProductType === ProductType.VEGETATIVE_INDEX
            ? isValueInBucketCropMonitoring(value, this.bucket[color])
            : isValueInBucket(value, this.bucket[color])
        ) {
          if (this.selectedType === this.TYPE.AREA) {
            let parcel;
            if (this.$store.state.analytic.selectedProductType === ProductType.VEGETATIVE_INDEX) {
              parcel = this.$store.getters.parcelsByName[analytic.ParcelID];
            } else {
              parcel = this.$store.state.parcels[analytic.ParcelID];
            }
            if (parcel) {
              total += parcel.Area;
            }
          } else {
            total += 1;
          }
        }
      });
      data[color] = total || null;
    });
    if (this.$store.state.analytic.selectedProductType !== ProductType.VEGETATIVE_INDEX) {
      if (this.selectedType !== this.TYPE.AREA) {
        const harvestedInfo = this.getHarvestedInfo();
        let newColor = '#D6D58E';
        data[newColor] = Object.keys(harvestedInfo.harvested).length;
      } else {
        const harvestedInfo = this.getHarvestedInfo();
        let newColor = '#D6D58E';
        let totalHarvestedParcel = 0;
        let harvestedParcelArea = Object.values(harvestedInfo.harvested);
        harvestedParcelArea.forEach((harvest) => {
          for (let key in harvest) {
            if (`${key}` === 'Area') {
              totalHarvestedParcel += harvest[key];
            }
          }
        });
        data[newColor] = totalHarvestedParcel;
      }
    }
    return data;
  }

  get bucketLabels(): string[] {
    let allLabels;
    if (this.$store.state.analytic.selectedProductType === ProductType.SUGAR_CONTENT_PREDICTION) {
      allLabels = Object.values(this.bucket).map((range) => `${range[0]} - ${range[1]} ${this.measure}`);
      allLabels.pop();
      allLabels.push('14.5 % and above');
    } else if (this.$store.state.analytic.selectedProductType === ProductType.SUGAR_CONTENT_YIELD) {
      allLabels = Object.values(this.bucket).map((range) => `${range[0]} - ${range[1]} ${this.measure}`);
      allLabels.pop();
      allLabels.push('140 ton/ha and above');
    } else if (this.$store.state.analytic.selectedProductType === ProductType.VEGETATIVE_INDEX) {
      allLabels = Object.values(this.bucket).map((range) => `${range[0]} - ${range[1]}%`);
    } else {
      allLabels = Object.values(this.bucket).map((range) => `${range[0]} - ${range[1]} ${this.measure}`);
    }
    return allLabels;
  }

  private getHarvestedInfo(): HarvestedInfo {
    if (
      this.$store.state.analytic.selectedProductType === ProductType.SUGAR_CONTENT_PREDICTION ||
      this.$store.state.analytic.selectedProductType === ProductType.SUGAR_CONTENT_YIELD
    ) {
      return this.getSugarContentHarvestedInfo();
    }
    return null;
  }

  private getSugarContentHarvestedInfo(): HarvestedInfo {
    const harvested = {};
    const momentTo = stringToMomentDate(this.$store.state.analytic.fromTo.to);
    this.$store.getters.filteredParcelIds.forEach((parcelId: string) => {
      const parcel = this.$store.state.parcels[parcelId] as Parcel;
      if (
        parcel &&
        parcel.HarvestDates &&
        parcel.HarvestDates.length &&
        parcelMatchFilters(parcel, this.$store.state.mapParcelFilters, this.$store.state.analytic.fromTo) &&
        momentTo.isSameOrAfter(stringToMomentDate(parcel.HarvestDates[0]))
      ) {
        harvested[parcel.id] = parcel;
      }
    });
    return {
      harvested,
      partialHarvested: null
    };
  }
}
